import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  ArrowDownLeft,
  ArrowRight,
  ArrowUpRight,
  Eye,
  PhoneMissed,
  Bot,
} from "lucide-react";
import moment from "moment";
import { isAfter, startOfDay } from "date-fns";
import type { DateRange } from "react-day-picker";
import { Badge } from "@/components/ui/badge";
import { formatDate, formatUSPhoneNumber } from "@/utils/helpers";
import { extractUnassignedInteractions } from "@/conflicts/api";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { patientV2BlueOutlineBadgeClass } from "../constants";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getReportDateRange, type ReportDatePreset } from "@/utils/helpers";
import { formatCallCardPhone, isGraceBotCall } from "@/conflicts/utils";
import { PATIENT_INTERACTIONS_API_URL } from "@/conflicts/constants";
import type { ConflictRow } from "@/conflicts/types";
import TableRowCardWithSearch from "./TableRowCardWithSearch";
import { CallRowCard } from "./CallRowCard";
import { CallDetailsDialog } from "./CallDetailsDialog";
import { PatientV2ExpandableCardActions } from "./PatientV2ExpandableCardActions";
import { CallTypeFilter } from "./CallTypeFilter";

const DATE_PRESET_OPTIONS: { value: ReportDatePreset; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 Days" },
  { value: "last15", label: "Last 15 Days" },
  { value: "last30", label: "Last 30 Days" },
  { value: "last90", label: "Last 90 Days" },
  { value: "custom", label: "Custom date range" },
];

// const DEFAULT_CALL_TYPES = ["inbound", "Outbound", "grace"];

const CALL_TYPE_FILTER_OPTIONS = [
  { label: "Outbound (not connected)", key: "outboundNotConnected" },
  // { label: "Outbound (Voicemail Left)"4444444444444, key: "outboundVoicemail" },
  { label: "Outbound Connected", key: "outboundConnected" },
  { label: "Missed and Transferred", key: "inboundMissedTransferred" },
  { label: "Missed", key: "inboundMissed" },
  { label: "Inbound Received", key: "inboundReceived" },
];

const DEFAULT_CALL_TYPES = CALL_TYPE_FILTER_OPTIONS.map((o) => o.key);

const CALL_COLUMNS = [
  { id: "date", label: "Date", isSortable: true },
  { id: "time", label: "Time", isSortable: false },
  { id: "phoneNumber", label: "Phone Number", isSortable: false },
  { id: "callType", label: "Call Type", isSortable: false },
  { id: "duration", label: "Duration", isSortable: false },
] as const;

type PersonName = { firstName?: string | null; lastName?: string | null };

/** One party on the call line (agent, patient, Aircall line or external number). */
type CallParty = {
  name?: string | null;
  role?: string | null;
  number?: string | null;
  externalNumber?: string | null;
  via?: string | null;
};

/** Aircall call fields read from the last stored webhook payload. */
type AircallCallPayload = {
  external_transferred_to?: string | null;
  number?: { name?: string | null; digits?: string | null } | null;
};

type CallRow = ConflictRow & {
  direction?: string;
  answeredBy?: CallParty | null;
  agentPath?: CallParty[];
  caretaker?: PersonName | null;
  caretakerName?: string | null;
  patient?: PersonName | null;
  rawPayload?: AircallCallPayload | null;
  callOutcome?: string | null;
};

type CallOutcome =
  | "connected"
  | "attempted"
  // | "voicemail_left"
  | "received"
  | "missed_transferred"
  | "missed";

const PILL_TONE = {
  green: "border-[#A7F3D0] bg-[#ECFDF5] text-[#047857]",
  amber: "border-[#FDE68A] bg-[#FFFBEB] text-[#B45309]",
  grey: "border-[#E5E7EB] bg-[#F3F4F6] text-[#4B5563]",
} as const;

function splitCallDateTime(row: ConflictRow) {
  const raw = row.callAt || row.createdAt;
  if (!raw) return { date: "—", time: "—" };
  const m = moment(raw);
  if (!m.isValid()) return { date: "—", time: "—" };
  return {
    date: formatDate(raw),
    time: m.format("hh:mm A"),
  };
}

function formatCallDuration(row: ConflictRow) {
  const seconds = Number(row.durationSeconds);
  if (Number.isFinite(seconds) && seconds >= 0) {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    if (m <= 0) return `${s}s`;
    return `${m}m ${String(s).padStart(2, "0")}s`;
  }
  if (row.duration) return String(row.duration);
  return "";
}

function personName(person?: PersonName | null) {
  return [person?.firstName, person?.lastName].filter(Boolean).join(" ").trim();
}

function isOutboundCall(row: CallRow) {
  const direction = String(row.direction || row.callType || "").toLowerCase();
  return direction === "outbound" || direction === "outgoing";
}

/** Agents in the order they handled the call; falls back to the single stored caretaker. */
function getAgentPath(row: CallRow): CallParty[] {
  if (Array.isArray(row.agentPath) && row.agentPath.length > 0) {
    return row.agentPath.map((agent) => ({
      ...agent,
      number: agent.number ?? agent.externalNumber ?? null,
    }));
  }
  const name =
    row.answeredBy?.name || personName(row.caretaker) || row.caretakerName;
  if (!name) return [];
  return [
    {
      name,
      role: row.answeredBy?.role ?? null,
      number: row.rawPayload?.number?.digits ?? null,
    },
  ];
}
/** Backend `callOutcome` → card outcome. */
const BACKEND_OUTCOME: Record<string, CallOutcome> = {
  outboundConnected: "connected",
  outboundNotConnected: "attempted",
  // outboundVoicemail: "voicemail_left",
  inboundReceived: "received",
  inboundMissedTransferred: "missed_transferred",
  inboundMissed: "missed",
};

/** Outcome comes only from the backend; null when it is missing or unknown. */
function getCallOutcome(row: CallRow): CallOutcome | null {
  return BACKEND_OUTCOME[String(row.callOutcome ?? "")] ?? null;
}

function agentLabel(agent?: CallParty | null) {
  if (!agent?.name) return "";
  return agent.role ? `${agent.name} · ${agent.role}` : agent.name;
}

function getOutcomePill(
  outcome: CallOutcome | null,
  row: CallRow,
  agents: CallParty[],
) {
  if (!outcome) return null;
  const answerer = agentLabel(row.answeredBy ?? agents[0]);
  const by = answerer ? ` by ${answerer}` : "";
  const lastAgent = agents[agents.length - 1];
  const transferTarget =
    lastAgent?.name ||
    lastAgent?.number ||
    row.rawPayload?.external_transferred_to ||
    "";

  switch (outcome) {
    case "connected":
      return { label: `Connected${by}`, tone: PILL_TONE.green };
    case "attempted":
      return { label: `Attempted${by} (Not Connected)`, tone: PILL_TONE.amber };
    // case "voicemail_left":
    //   return { label: `Left Voicemail${by}`, tone: PILL_TONE.grey };
    case "received":
      return { label: `Received${by}`, tone: PILL_TONE.green };
    case "missed_transferred":
      return {
        label: transferTarget
          ? `Missed and Transferred to ${transferTarget}`
          : "Missed and Transferred",
        tone: PILL_TONE.grey,
      };
    default:
      return { label: "Missed - Short Call", tone: PILL_TONE.amber };
  }
}

/** Ordered parties for the title line: outbound = agents → patient, inbound = patient → agents. */
function getCallParties(row: CallRow, agents: CallParty[]): CallParty[] {
  const patient: CallParty = {
    name: personName(row.patient) || null,
    number: row.externalNumber || row.phoneNumber || null,
  };
  const externalTransfer =
    !row.agentPath?.length && row.rawPayload?.external_transferred_to
      ? [{ name: "External", number: row.rawPayload.external_transferred_to }]
      : [];
  const path = [...agents, ...externalTransfer];

  if (isOutboundCall(row)) return [...path, patient];
  if (path.length > 0) return [patient, ...path];

  const line = row.rawPayload?.number;
  return line?.name || line?.digits
    ? [patient, { name: line.name ?? null, number: line.digits ?? null }]
    : [patient];
}

function getCallIcon(
  outcome: CallOutcome | null,
  outbound: boolean,
): { icon: ReactNode; bg: string } {
  if (outbound) {
    return {
      icon: (
        <ArrowUpRight className="h-4 w-4 text-[#2563EB]" strokeWidth={1.75} />
      ),
      bg: "bg-[#EFF6FF]",
    };
  }
  if (outcome === "received" || outcome === "missed_transferred") {
    return {
      icon: (
        <ArrowDownLeft className="h-4 w-4 text-[#059669]" strokeWidth={1.75} />
      ),
      bg: "bg-[#ECFDF5]",
    };
  }
  if (!outcome) {
    return {
      icon: (
        <ArrowDownLeft
          className="h-4 w-4 text-muted-foreground"
          strokeWidth={1.75}
        />
      ),
      bg: "bg-[#F3F4F6]",
    };
  }
  return {
    icon: <PhoneMissed className="h-4 w-4 text-[#DC2626]" strokeWidth={1.75} />,
    bg: "bg-[#FEF2F2]",
  };
}

/** Everything the card and the Full Details popup show for one call, so both always agree. */
function buildCallView(call: CallRow) {
  const outbound = isOutboundCall(call);
  const agents = getAgentPath(call);
  const outcome = getCallOutcome(call);
  return {
    outbound,
    agents,
    pill: getOutcomePill(outcome, call, agents),
    parties: getCallParties(call, agents),
    callIcon: getCallIcon(outcome, outbound),
  };
}

export function CallRecordingsTab({
  patientId,
  enabled,
}: {
  patientId: string;
  enabled: boolean;
}) {
  const [rows, setRows] = useState<ConflictRow[]>([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsRow, setDetailsRow] = useState<ConflictRow | null>(null);
  const [datePreset, setDatePreset] = useState<ReportDatePreset>("last30");
  const [customRange, setCustomRange] = useState<DateRange | undefined>();
  const [draftCustomRange, setDraftCustomRange] = useState<
    DateRange | undefined
  >();
  const [customPickerOpen, setCustomPickerOpen] = useState(false);
  const [activeRangeBoundary, setActiveRangeBoundary] = useState<
    "from" | "to" | null
  >(null);
  const [callTypes, setCallTypes] = useState<string[]>(DEFAULT_CALL_TYPES);

  const { startDate, endDate } = useMemo(
    () =>
      getReportDateRange(
        datePreset,
        customRange?.from,
        customRange?.to ?? customRange?.from,
      ),
    [datePreset, customRange],
  );

  const selectedCallTypes = callTypes;

  const listParams = useMemo(
    () => ({
      patientId: patientId || undefined,
      startDate,
      endDate,
      ...(selectedCallTypes.length > 0
        ? { callType: selectedCallTypes.join(",") }
        : {}),
    }),
    [patientId, startDate, endDate, selectedCallTypes],
  );

  const isFutureCalendarDay = (d: Date) =>
    isAfter(startOfDay(d), startOfDay(new Date()));

  const handleCustomRangeSelect = (
    _next: DateRange | undefined,
    day?: Date,
  ) => {
    if (!day || isFutureCalendarDay(day)) return;

    setDraftCustomRange((prev) => {
      const from = prev?.from;
      const to = prev?.to;

      if (!from || !to) {
        if (from && !to) {
          setActiveRangeBoundary(null);
          return day < from ? { from: day, to: from } : { from, to: day };
        }
        setActiveRangeBoundary("to");
        return { from: day, to: undefined };
      }

      if (day.toDateString() === from.toDateString()) {
        setActiveRangeBoundary("from");
        return prev;
      }
      if (day.toDateString() === to.toDateString()) {
        setActiveRangeBoundary("to");
        return prev;
      }

      if (activeRangeBoundary === "from") {
        setActiveRangeBoundary(null);
        return day <= to ? { from: day, to } : { from: to, to: day };
      }
      if (activeRangeBoundary === "to") {
        setActiveRangeBoundary(null);
        return day >= from ? { from, to: day } : { from: day, to: from };
      }

      setActiveRangeBoundary("to");
      return { from: day, to: undefined };
    });
  };

  const handleCustomPickerOpenChange = (open: boolean) => {
    setCustomPickerOpen(open);
    if (open) {
      setDraftCustomRange(customRange);
      setActiveRangeBoundary(null);
      return;
    }
    setCustomRange(draftCustomRange);
    setActiveRangeBoundary(null);
  };

  const resetFilters = () => {
    setDatePreset("last30");
    setCustomRange(undefined);
    setDraftCustomRange(undefined);
    setCustomPickerOpen(false);
    setActiveRangeBoundary(null);
    setCallTypes(DEFAULT_CALL_TYPES);
  };

  const filtersToolbar = (
    <div className="grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 sm:flex sm:w-auto sm:shrink-0 sm:justify-end">
      <CallTypeFilter
        options={CALL_TYPE_FILTER_OPTIONS}
        value={callTypes}
        onChange={setCallTypes}
        placeholder="Call Type"
      />
      <Select
        value={datePreset}
        onValueChange={(value) => {
          const preset = value as ReportDatePreset;
          setDatePreset(preset);
          if (preset !== "custom") {
            setCustomPickerOpen(false);
            setActiveRangeBoundary(null);
          } else {
            handleCustomPickerOpenChange(true);
          }
        }}
      >
        <SelectTrigger
          size="sm"
          className="w-full min-w-0 rounded-lg border-gray-300 bg-white text-xs sm:w-auto sm:min-w-[130px] sm:shrink-0"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DATE_PRESET_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {datePreset === "custom" ? (
        <Popover
          open={customPickerOpen}
          onOpenChange={handleCustomPickerOpenChange}
        >
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="order-last col-span-3 h-8 shrink-0 whitespace-nowrap rounded-lg text-xs sm:order-0 sm:col-auto"
            >
              {draftCustomRange?.from && draftCustomRange?.to
                ? `${draftCustomRange.from.toLocaleDateString()} — ${draftCustomRange.to.toLocaleDateString()}`
                : draftCustomRange?.from
                  ? `${draftCustomRange.from.toLocaleDateString()} — …`
                  : "Pick dates"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              numberOfMonths={2}
              selected={draftCustomRange}
              onSelect={handleCustomRangeSelect as never}
              defaultMonth={draftCustomRange?.from}
              disabled={(date) => isFutureCalendarDay(date)}
            />
          </PopoverContent>
        </Popover>
      ) : null}
      <Button
        type="button"
        variant="default"
        size="sm"
        className="h-8 shrink-0 rounded-lg text-xs"
        onClick={resetFilters}
      >
        Reset
      </Button>
    </div>
  );

  const handleSetRows = (payload: unknown) => {
    setRows(extractUnassignedInteractions(payload));
  };

  const viewData = useMemo(
    () =>
      rows.map((row) => {
        const { date, time } = splitCallDateTime(row);
        return {
          ...row,
          date,
          time,
          phoneNumber: formatCallCardPhone(row),
          callType: String(row.callType || "").trim() || "—",
          duration: formatCallDuration(row) || "—",
        };
      }),
    [rows],
  );

  const openDetails = (row: ConflictRow) => {
    setDetailsRow(row);
    setDetailsOpen(true);
  };

  const detailsCall = detailsRow as CallRow | null;
  const detailsView = detailsCall ? buildCallView(detailsCall) : null;

  return (
    <div className="teachers">
      <TableRowCardWithSearch
        heading="Calls"
        url={PATIENT_INTERACTIONS_API_URL}
        params={listParams}
        apiDataKey=""
        fetchCondition={enabled && !!patientId}
        isIncludeSearch={false}
        isIgnoreSearchParams
        searchPlaceholder="Search by phone number"
        columnHeaders={CALL_COLUMNS as never}
        data={rows as never[]}
        viewData={viewData}
        setData={handleSetRows}
        isActionColumn={false}
        handleAction={() => {}}
        filtersOptions={[]}
        filters={{}}
        setFilters={() => {}}
        headerExtraControls={filtersToolbar}
        cardContainerClassName="!overflow-visible !rounded-none !border-0 !bg-transparent !divide-y-0 space-y-3"
        clickRow={() => {}}
        moreActions={[
          {
            label: "View Full Details",
            icon: <Eye className="h-4 w-4" />,
            isShow: () => true,
            onClick: (row: ConflictRow) => openDetails(row),
          },
        ]}
        key={`call-recordings-${patientId}-${startDate}-${endDate}-${selectedCallTypes.join(",")}`}
        renderRowCard={({ viewRow, row, actions }) => {
          const call = row as CallRow;
          const v = viewRow as Record<string, unknown>;
          const date = String(v?.date || "—");
          const time = String(v?.time || "—");
          const isGraceCall = isGraceBotCall(row);

          const duration = String(v?.duration || "").trim();
          const dateTimeLabel = [date, time]
            .filter((part) => part && part !== "—")
            .join(" ");

          const { pill, parties, callIcon } = buildCallView(call);

          return (
            <CallRowCard
              icon={callIcon.icon}
              iconBgClass={callIcon.bg}
              title={
                <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[13px] leading-snug">
                  {parties.map((party, idx) => {
                    const number = party.number
                      ? formatUSPhoneNumber(party.number)
                      : "";
                    return (
                      <span
                        key={`${idx}-${party.name ?? ""}-${party.number ?? ""}`}
                        className="inline-flex min-w-0 flex-wrap items-center gap-x-1.5"
                      >
                        {idx > 0 ? (
                          <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
                        ) : null}
                        <span className="wrap-break-word font-semibold text-foreground">
                          {party.name || number || "—"}
                        </span>
                        {party.name && number ? (
                          <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                            {number}
                          </span>
                        ) : null}
                      </span>
                    );
                  })}
                </div>
              }
              meta={
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                  {pill ? (
                    <Badge
                      variant="outline"
                      className={`shrink-0 rounded-full px-2 py-0 text-[11px] font-medium leading-5 ${pill.tone}`}
                    >
                      {pill.label}
                    </Badge>
                  ) : null}
                  {dateTimeLabel ? (
                    <span>
                      <span className="font-semibold uppercase tracking-wide">
                        Date:
                      </span>{" "}
                      <span className="text-foreground">{dateTimeLabel}</span>
                    </span>
                  ) : null}
                </div>
              }
              actions={
                <div className="flex items-center gap-2">
                  {duration && duration !== "—" ? (
                    <Badge
                      variant="outline"
                      className={`shrink-0 px-2 py-0.5 text-[11px] font-semibold ${PILL_TONE.grey}`}
                    >
                      {duration}
                    </Badge>
                  ) : null}
                  <PatientV2ExpandableCardActions actions={actions} row={row} />
                  {/* {isGraceCall ? (
                    <Badge
                      variant="outline"
                      className={`${patientV2BlueOutlineBadgeClass} font-semibold`}
                    >
                      <Bot className="h-4 w-4 text-[#1D4ED8]" />
                      Grace
                    </Badge>
                  ) : null} */}
                </div>
              }
            />
          );
        }}
      />

      {detailsCall && detailsView ? (
        <CallDetailsDialog
          open={detailsOpen}
          onOpenChange={setDetailsOpen}
          call={detailsCall}
          icon={detailsView.callIcon.icon}
          iconBgClass={detailsView.callIcon.bg}
          outbound={detailsView.outbound}
          pill={detailsView.pill}
          parties={detailsView.parties}
          agentName={detailsView.agents[0]?.name}
          patientName={personName(detailsCall.patient) || null}
        />
      ) : null}
    </div>
  );
}
